import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, Calendar, SlidersHorizontal, Settings } from 'lucide-react'

interface MobileFooterProps {
    /** Optional — omit for dashboards without single-date navigation (e.g. custom range) */
    dateType?: 'date' | 'month'
    selectedDate?: string
    setSelectedDate?: (date: string) => void
    shiftDate?: (amount: number) => void
    isCurrent?: boolean
    customDateSelector?: React.ReactNode

    channel: string
    setChannel: (channel: string) => void

    includeReturns: boolean
    setIncludeReturns: (val: boolean) => void

    includeMultibuys: boolean
    setIncludeMultibuys: (val: boolean) => void

    compareFull: boolean
    setCompareFull: (val: boolean) => void

    onActionClick: () => void
}

export default function MobileFooter(props: MobileFooterProps) {
    const [filtersOpen, setFiltersOpen] = useState(false)
    const dateInputRef = useRef<HTMLInputElement>(null)
    const trayRef = useRef<HTMLDivElement>(null)
    const [trayHeight, setTrayHeight] = useState(0)

    useEffect(() => {
        if (!trayRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setTrayHeight(entry.target.getBoundingClientRect().height);
            }
        });
        observer.observe(trayRef.current);
        return () => observer.disconnect();
    }, []);

    const handleCalendarClick = () => {
        if (dateInputRef.current && 'showPicker' in HTMLInputElement.prototype) {
            try {
                dateInputRef.current.showPicker()
            } catch (e) {
                dateInputRef.current.focus()
            }
        } else if (dateInputRef.current) {
            dateInputRef.current.focus()
        }
    }

    const footerContent = (
        <>
            <div className={`mobile-filters-backdrop ${filtersOpen ? 'open' : ''}`} onClick={() => setFiltersOpen(false)} />

            {/* Filters Tray slides up from bottom */}
            <div
                ref={trayRef}
                className={`mobile-filters-tray ${filtersOpen ? 'open' : ''}`}
            >
                <div className="mobile-filters-tray-inner" style={{ paddingTop: '32px' }}>
                    <div className="filter-group">
                        <div className="seg-control">
                            {(['all', 'instore', 'online'] as const).map(ch => (
                                <button key={ch} className={props.channel === ch ? 'active' : ''}
                                    onClick={() => props.setChannel(ch)}>
                                    {ch === 'all' ? 'ALL' : ch === 'instore' ? 'IN-STORE' : 'ONLINE'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="filter-group">
                        <div className="seg-control">
                            <button className={props.includeReturns ? 'active' : ''} onClick={() => props.setIncludeReturns(true)}>NET</button>
                            <button className={!props.includeReturns ? 'active' : ''} onClick={() => props.setIncludeReturns(false)}>GROSS</button>
                        </div>
                    </div>

                    <div className="filter-toggles-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px', alignItems: 'flex-start' }}>
                        <label className="toolbar-checkbox" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-text)', fontSize: '15px' }}>
                            <input
                                type="checkbox"
                                checked={props.includeMultibuys}
                                onChange={(e) => props.setIncludeMultibuys(e.target.checked)}
                            />
                            Include Multibuys
                        </label>

                        <label className="toolbar-checkbox" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-text)', fontSize: '15px' }}>
                            <input
                                type="checkbox"
                                checked={props.compareFull}
                                onChange={(e) => props.setCompareFull(e.target.checked)}
                            />
                            Compare Full
                        </label>
                    </div>
                </div>
            </div>

            <div className="mobile-footer">
                <div className="mobile-footer-bg" />
                <div className="mobile-footer-border-left" />
                <div className="mobile-footer-border-right" />
                <div className="mobile-footer-border-arc" />

                {props.shiftDate ? (
                    <button className="mobile-footer-btn" onClick={() => props.shiftDate!(-1)}>
                        <ChevronLeft size={24} />
                    </button>
                ) : <div style={{ flex: 1 }} />}

                {props.shiftDate ? (
                    <button className="mobile-footer-btn calendar-btn-wrapper" onClick={handleCalendarClick}>
                        <Calendar size={24} />
                        <input
                            ref={dateInputRef}
                            type={props.dateType}
                            value={props.selectedDate}
                            onChange={(e) => props.setSelectedDate?.(e.target.value)}
                            className="mobile-hidden-date-input"
                        />
                    </button>
                ) : props.customDateSelector ? (
                    <div className="mobile-footer-btn" style={{ position: 'relative' }}>
                        {props.customDateSelector}
                    </div>
                ) : <div style={{ flex: 1 }} />}

                {/* Visual Gap/Cutout in Footer placeholder for FAB */}
                <div className="fab-footer-gap-container" onClick={() => setFiltersOpen(!filtersOpen)}>
                    {/* Border arc draws this gap dynamically now */}
                </div>

                <button className="mobile-footer-btn" onClick={() => {
                    setFiltersOpen(false)
                    props.onActionClick()
                }}>
                    <Settings size={24} />
                </button>

                {props.shiftDate ? (
                    <button className="mobile-footer-btn" onClick={() => props.shiftDate!(1)}>
                        <ChevronRight size={24} />
                    </button>
                ) : <div style={{ flex: 1 }} />}
            </div>

            {/* The FAB acts as a visual seal for the tray but lives outside it to hover above the footer */}
            <div
                className={`tray-fab-container ${filtersOpen ? 'open' : ''}`}
                style={{ transform: `translateX(-50%) translateY(${filtersOpen ? -(trayHeight - 80) : 0}px)` }}
            >
                <button
                    className={`tray-fab ${filtersOpen ? 'active' : ''}`}
                    onClick={() => setFiltersOpen(!filtersOpen)}
                >
                    <SlidersHorizontal size={24} />
                </button>
            </div>
        </>
    )

    return createPortal(footerContent, document.body)
}
